import { useMutation } from "@tanstack/react-query";
import {
  AlertTriangle,
  BookOpen,
  Check,
  ChevronRight,
  ExternalLink,
  FilePen,
  FolderTree,
  GitBranch,
  Link2,
  MessageCircleQuestion,
  Orbit,
  Pencil,
  Pin,
  Sparkles,
  StickyNote,
  Trash2,
  Wand2,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { EmptyState, ErrorState, LoadingState } from "@/components/feedback";
import { MetadataSection } from "@/components/rail/MetadataSection";
import { Section, SectionDivider } from "@/components/rail/InspectorSections";
import { workspace } from "@/components/workspace/controller";
import { ConnectionsList } from "@/components/rail/ConnectionsList";
import { useFileReveal } from "@/stores/file-reveal";
import { Button } from "@/components/ui/button";
import { Tip } from "@/components/ui/overlays";
import { Badge, Input, Textarea } from "@/components/ui/primitives";
import { keys, useGraphOntology, useNode, useSearch } from "@/hooks/queries";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { trackJob } from "@/hooks/use-upload";
import { api, queryClient, type EdgeOut } from "@/lib/api";
import { authorableTypes, edgePhrase } from "@/lib/ontology";
import { humanizeEdgeType, NODE_COLOR, NODE_LABELS } from "@/lib/palette";
import { cn, truncate } from "@/lib/utils";
import { linkWikilinks } from "@/lib/wikilinks";
import { useGraph } from "@/stores/graph";
import { useShell } from "@/stores/shell";
import { useTabs } from "@/stores/tabs";

// The base Button variants carry no focus ring; add one for the icon-only
// header controls so keyboard users get a visible focus target (a11y).
const iconFocus = "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";

/**
 * The Inspector's header collapse — the missing sibling of every other bar's
 * collapse control. It folds the right area away by REUSING the shells' own
 * closers (no new state): `toggleRightBar` is exactly what the desktop
 * `appshell/RightBar` collapse button calls, and `closeRight` dismisses the
 * compact drawer that also embeds this panel — so the one button honestly
 * collapses whichever shell mounted it. Chevron idiom + Tip match the RightBar.
 */
function CollapseButton() {
  return (
    <Tip label="Collapse" side="left">
      <Button
        size="icon"
        variant="ghost"
        aria-label="Collapse inspector"
        onClick={() => {
          const s = useShell.getState();
          s.toggleRightBar();
          s.closeRight();
        }}
        className={cn("h-7 w-7 shrink-0", iconFocus)}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </Tip>
  );
}

export function InspectorPanel() {
  const selection = useGraph((s) => s.selection);
  if (!selection) {
    // The empty state is the only place the panel gets to SAY what it is for,
    // and it was spending that on atlas mouse gestures — which taught you how
    // to select something without telling you what selecting something here
    // would give you.
    return (
      <EmptyState
        icon={Sparkles}
        title="Nothing selected"
        description="Pick a note or a connection to see its details, edit its description and tags, and act on it. Click a star in the atlas, or a file in the explorer."
        className="pt-10"
      />
    );
  }
  return selection.kind === "node" ? (
    <NodeInspector nodeId={selection.id} />
  ) : (
    <EdgeInspector edgeId={selection.id} />
  );
}

function NodeInspector({ nodeId }: { nodeId: string }) {
  const node = useNode(nodeId);
  const openReader = useTabs((s) => s.openReader);
  const openAsk = useTabs((s) => s.openAsk);
  const [note, setNote] = useState("");
  const [renaming, setRenaming] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [fixSteer, setFixSteer] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const deleteIntent = useGraph((s) => s.deleteIntent);

  // The Del key opens this confirm — but only for a single selection; a
  // multi-selection is handled by the graph's bulk bar.
  useEffect(() => {
    if (deleteIntent > 0 && useGraph.getState().multiSelected.size === 0) {
      setConfirmingDelete(true);
    }
  }, [deleteIntent]);

  const refreshGraph = () => {
    void queryClient.invalidateQueries({ queryKey: keys.node(nodeId) });
    void queryClient.invalidateQueries({ queryKey: keys.overview });
    // A rename / added note / refined title changes what the file explorer shows;
    // invalidate the vault tree so it updates at once instead of waiting on its
    // 30s poll (Part D — the explorer must reflect what the user just did).
    void queryClient.invalidateQueries({ queryKey: keys.vault });
  };

  const rename = useMutation({
    mutationFn: (label: string) => api.request("PATCH", `/api/nodes/${nodeId}`, { label }),
    onSuccess: () => {
      setRenaming(null);
      toast.success("Renamed");
      refreshGraph();
    },
    onError: (e) => toast.error("Could not rename", { description: String(e) }),
  });

  const pin = useMutation({
    mutationFn: async () => {
      if (!node.data) return;
      await api.request("POST", "/api/workspace/items", {
        kind: "node",
        title: node.data.label,
        ref_id: node.data.id,
        // Attach to the active Ask conversation (null → Unsorted bucket).
        conversation_id: useTabs.getState().activeConversationId() ?? null,
      });
    },
    onSuccess: () => {
      const inConvo = !!useTabs.getState().activeConversationId();
      toast.success(inConvo ? "Pinned to this conversation" : "Pinned (unsorted)");
      void queryClient.invalidateQueries({ queryKey: keys.workspace });
    },
  });

  // Explicit, user-triggered atlas reorganization around an org hub —
  // sweeps existing documents for tag/metadata matches. Never automatic.
  const reorganize = useMutation({
    mutationFn: async () => {
      const { job_id } = await api.request<{ job_id: string }>(
        "POST",
        `/api/graph/org/${nodeId}/reorganize`,
      );
      return trackJob(job_id);
    },
    onSuccess: (job) => {
      const result = job.result as { attached?: number; checkpoint_id?: string } | null;
      const attached = result?.attached ?? 0;
      const checkpoint = result?.checkpoint_id;
      void queryClient.invalidateQueries({ queryKey: keys.overview });
      // Always offer an Undo: a reorganize can be large and is easy to trigger
      // by accident, so the pre-reorganize checkpoint is one click away.
      toast.success(
        attached > 0
          ? `Gathered ${attached} document${attached === 1 ? "" : "s"}`
          : "Nothing new matched this node's tags",
        checkpoint
          ? {
              duration: 12_000,
              action: {
                label: "Undo",
                onClick: () =>
                  void api
                    .request("POST", `/api/graph/history/${checkpoint}/restore`)
                    .then(() => {
                      toast("Reorganize undone");
                      void queryClient.invalidateQueries({ queryKey: keys.overview });
                    })
                    .catch((e) => toast.error("Undo failed", { description: String(e) })),
              },
            }
          : undefined,
      );
    },
    onError: (e) => toast.error("Reorganize failed", { description: String(e) }),
  });

  const addNote = useMutation({
    mutationFn: async () => {
      const n = node.data!;
      const created = (await api.createNode({
        type: "note",
        label: truncate(note, 60),
        text: note,
        document_id: n.document_id,
      } as never)) as { id: string };
      // Tie the note to what it annotates.
      await api.createEdge({ src_id: created.id, dst_id: n.id, type: "about" } as never);
      // Obsidian-style: any [[Title]] in the note auto-links to that node.
      const links = await linkWikilinks(created.id, note, "about");
      return { created, links };
    },
    onSuccess: ({ created, links }) => {
      setNote("");
      toast.success(links > 0 ? `Note saved · ${links} link${links > 1 ? "s" : ""} formed` : "Note saved to graph + vault");
      refreshGraph();
      useGraph.getState().requestFocus(created.id);
    },
    onError: (e) => toast.error("Could not save note", { description: String(e) }),
  });

  const fixMetadata = useMutation({
    mutationFn: async () => {
      const { job_id } = (await api.request("POST", `/api/documents/${node.data!.document_id}/refine-metadata`, {
        steer: fixSteer ?? "",
      })) as { job_id: string };
      return trackJob(job_id);
    },
    onSuccess: () => {
      setFixSteer(null);
      toast.success("Metadata refreshed");
      refreshGraph();
    },
    onError: (e) => toast.error("Could not refine metadata", { description: String(e) }),
  });

  const del = useMutation({
    mutationFn: () => {
      const n = node.data!;
      // Deleting a document removes the whole document (so it leaves the atlas
      // AND can be re-uploaded); other nodes just delete that node.
      return n.type === "document" && n.document_id
        ? api.request("DELETE", `/api/documents/${n.document_id}`)
        : api.request("DELETE", `/api/nodes/${nodeId}`);
    },
    onSuccess: () => {
      const n = node.data;
      useGraph.getState().removeNode(nodeId);
      // Close any reader/ask tabs tied to the deleted node.
      useTabs.getState().closeForNode(nodeId, n?.type === "document" ? n.document_id : null);
      toast.success("Deleted");
      void queryClient.invalidateQueries({ queryKey: keys.overview });
      void queryClient.invalidateQueries({ queryKey: keys.documents });
      // The deleted document/note leaves the file explorer too — invalidate the
      // vault tree so its row disappears at once, not on the next 30s poll (Part D).
      void queryClient.invalidateQueries({ queryKey: keys.vault });
      // The pin(s) for this node were removed server-side — refresh the panels.
      void queryClient.invalidateQueries({ queryKey: keys.workspace });
      void queryClient.invalidateQueries({ queryKey: keys.projects });
    },
    onError: (e) => toast.error("Could not delete", { description: String(e) }),
  });

  const conceptBranch = useMutation({
    mutationFn: () =>
      api.request<import("@/lib/api").Neighborhood>(
        "GET",
        `/api/graph/concept-branch?node_id=${encodeURIComponent(nodeId)}`,
      ),
    onSuccess: (nb) => {
      const fresh = useGraph.getState().mergeBatch(nb.nodes, nb.edges);
      useGraph.getState().markFlash(fresh.length ? fresh : nb.nodes.map((n) => n.id));
      toast.success(nb.nodes.length ? `Branched ${nb.nodes.length} concept(s)` : "No concepts yet");
    },
    onError: (e) => toast.error("Could not branch concepts", { description: String(e) }),
  });

  // Opt-in concept generation. Concepts are OFF by default (file-first), so
  // this is how a user asks the assistant to extract idea nodes from a
  // document; the freshly-suggested concepts are then branched onto the canvas.
  const conceptualize = useMutation({
    mutationFn: async () => {
      const { job_id } = (await api.request(
        "POST",
        `/api/documents/${node.data!.document_id}/conceptualize`,
      )) as { job_id: string };
      return trackJob(job_id);
    },
    onSuccess: (job) => {
      if (job.status === "failed") {
        toast.error("Could not generate concepts", { description: job.error ?? "" });
        return;
      }
      const result = job.result as { concepts_created?: number; concepts_reused?: number } | null;
      const made = (result?.concepts_created ?? 0) + (result?.concepts_reused ?? 0);
      refreshGraph();
      // Branch concepts onto the canvas; report what actually came back.
      conceptBranch.mutate();
      if (made === 0) {
        toast("No concepts found in this document", {
          description: "The model didn't surface distinct ideas to add.",
        });
      } else {
        toast.success(`Generated ${made} concept${made === 1 ? "" : "s"}`);
      }
    },
    onError: (e) => toast.error("Could not generate concepts", { description: String(e) }),
  });

  const openSource = async () => {
    // Open the original file in the user's default app (local-first tool).
    const res = await api.request<{ opened: boolean; url?: string }>(
      "POST",
      `/api/documents/${node.data!.document_id}/open`,
    );
    if (res.opened) {
      toast.success("Opened on your device");
    } else if (res.url) {
      window.open(res.url, "_blank");
    } else {
      toast.warning("Could not open the source file");
    }
  };

  if (node.isLoading) {
    return <LoadingState variant="skeleton" rows={3} className="p-4" />;
  }
  if (node.isError) {
    return (
      <ErrorState
        error={node.error}
        onRetry={() => void node.refetch()}
        label="inspector"
        className="p-4"
      />
    );
  }
  if (!node.data) return null;
  const n = node.data;
  const isDoc = n.type === "document";
  const isOrg = n.type === "org";
  const isMarkdown = n.type === "note" || n.type === "ai_output" || n.type === "org";
  const metaKind: "document" | "node" = isDoc && n.document_id ? "document" : "node";
  const metaId = isDoc && n.document_id ? n.document_id : n.id;

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* IDENTITY — what this is and what it is called. The raw id used to sit
          here at the same weight as the type badge; it is a debugging detail,
          so it moved into that badge's tooltip. */}
      <div className="flex items-center gap-2">
        <Tip label={`id ${n.id}`}>
          <span>
            <Badge color={NODE_COLOR[n.type]}>{NODE_LABELS[n.type] ?? n.type}</Badge>
          </span>
        </Tip>
        {n.status === "suggested" && (
          <Badge className="border-primary/40 text-primary">suggested</Badge>
        )}
        <div className="ml-auto">
          <CollapseButton />
        </div>
      </div>

      {renaming === null ? (
        <div className="group flex items-start gap-1.5">
          <h2 className="font-display text-[19px] font-medium leading-snug text-foreground">
            {n.title || n.label}
          </h2>
          <Tip label="Rename">
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setRenaming(n.label)}
              aria-label="Rename"
              className={cn(
                "mt-0.5 h-6 w-6 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100",
                iconFocus,
              )}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </Tip>
        </div>
      ) : (
        <div className="flex items-center gap-1.5">
          <Input
            autoFocus
            value={renaming}
            onChange={(e) => setRenaming(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && renaming.trim()) rename.mutate(renaming.trim());
              if (e.key === "Escape") setRenaming(null);
            }}
          />
          <Button
            size="icon"
            variant="ghost"
            disabled={!renaming.trim() || rename.isPending}
            onClick={() => rename.mutate(renaming.trim())}
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button size="icon" variant="ghost" onClick={() => setRenaming(null)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* PRIMARY ACTIONS — the one or two verbs that matter for this type, at
          full width. "Read" previously sat in a grid cell the same size and
          weight as "Concept branch". */}
      <div className="flex gap-1.5">
        {isDoc && n.document_id && (
          <>
            <Button size="md" className="flex-1" onClick={() => openReader(n.document_id!, n.label)}>
              <BookOpen className="h-4 w-4" /> Read
            </Button>
            <Button
              size="md"
              variant="outline"
              className="flex-1"
              onClick={() => openAsk({ scopeDocIds: [n.document_id!], title: `ask · ${n.label}` })}
            >
              <MessageCircleQuestion className="h-4 w-4" /> Ask this
            </Button>
          </>
        )}
        {isMarkdown && (
          <>
            <Button
              size="md"
              className="flex-1"
              onClick={() => useTabs.getState().openMarkdown(n.id, n.label)}
            >
              <FilePen className="h-4 w-4" /> Edit
            </Button>
            <Button
              size="md"
              variant="outline"
              className="flex-1"
              onClick={() =>
                openAsk({
                  seedQuery: `Explain "${n.label}" using my atlas.`,
                  title: `ask · ${truncate(n.label, 16)}`,
                })
              }
            >
              <MessageCircleQuestion className="h-4 w-4" /> Ask about
            </Button>
          </>
        )}
        {!isDoc && !isMarkdown && (
          <Button
            size="md"
            variant="outline"
            className="flex-1"
            onClick={() =>
              openAsk({
                seedQuery: `Explain "${n.label}" using my atlas.`,
                title: `ask · ${truncate(n.label, 16)}`,
              })
            }
          >
            <MessageCircleQuestion className="h-4 w-4" /> Ask about
          </Button>
        )}
      </div>

      {isOrg && (
        <Tip label="Sweep existing documents and gather the ones matching this hub's tags — only happens when you ask">
          <Button
            size="md"
            variant="outline"
            className="w-full"
            style={{ color: NODE_COLOR.org }}
            onClick={() => reorganize.mutate()}
            disabled={reorganize.isPending}
          >
            <Orbit className="h-4 w-4" />
            {reorganize.isPending ? "Reorganizing…" : "Reorganize"}
          </Button>
        </Tip>
      )}

      <SectionDivider />

      {/* METADATA — editable at last. `summary`/`keywords` were rendered
          read-only here, and the only way to change a description was to ask a
          model to regenerate it. */}
      <MetadataSection kind={metaKind} itemId={metaId} canGenerate={isMarkdown} />
      {isDoc && n.document_id && (
        <div className="space-y-1.5">
          <Button
            size="md"
            variant="ghost"
            className="w-full justify-start text-muted-foreground hover:text-foreground"
            onClick={() => setFixSteer((v) => (v === null ? "" : null))}
          >
            <Wand2 className="h-4 w-4" /> Regenerate from the document…
          </Button>
          {fixSteer !== null && (
            <form
              className="space-y-1.5 rounded-md border border-border bg-muted/40 p-2"
              onSubmit={(e) => {
                e.preventDefault();
                fixMetadata.mutate();
              }}
            >
              <Input
                autoFocus
                value={fixSteer}
                onChange={(e) => setFixSteer(e.target.value)}
                placeholder="How should it change? (optional)"
              />
              <Button size="md" className="w-full" disabled={fixMetadata.isPending}>
                {fixMetadata.isPending ? "Refreshing…" : "Regenerate summary · keywords"}
              </Button>
            </form>
          )}
        </div>
      )}

      <SectionDivider />

      {/* RELATIONSHIPS — every graph operation together, because they are one
          kind of thing. Collapsed: useful, but not what the panel is for. */}
      <Section title="Relationships" collapsible defaultOpen={false}>
        <Button
          size="md"
          variant="ghost"
          className="w-full justify-start"
          onClick={() => setConnecting((v) => !v)}
        >
          <Link2 className="h-4 w-4" /> Connect to…
        </Button>
        {connecting && (
          <ConnectBox
            sourceId={n.id}
            onDone={() => {
              setConnecting(false);
              refreshGraph();
            }}
          />
        )}
        {isDoc && (
          <>
            <Tip label="Reveal the concepts that branch off this document">
              <Button
                size="md"
                variant="ghost"
                className="w-full justify-start"
                onClick={() => conceptBranch.mutate()}
                disabled={conceptBranch.isPending}
              >
                <GitBranch className="h-4 w-4" /> Concept branch
              </Button>
            </Tip>
            <Tip label="Generate concept nodes from this document (off by default — file-first)">
              <Button
                size="md"
                variant="ghost"
                className="w-full justify-start"
                onClick={() => conceptualize.mutate()}
                disabled={conceptualize.isPending}
              >
                <Sparkles className="h-4 w-4" />
                {conceptualize.isPending ? "Generating…" : "Conceptualize"}
              </Button>
            </Tip>
          </>
        )}
      </Section>

      <SectionDivider />

      {/* The neighbours the canvas no longer paints. See ConnectionsList. */}
      <ConnectionsList nodeId={nodeId} />

      <SectionDivider />

      <Section title="Organise" collapsible defaultOpen={false}>
        <Button
          size="md"
          variant="ghost"
          className="w-full justify-start"
          onClick={() => pin.mutate()}
          disabled={pin.isPending}
        >
          <Pin className="h-4 w-4" /> Pin
        </Button>
        {/* It used to say "Move or recolour in Folder View" and then just open
            the pane. Two problems: recolour is a FOLDER property, not a file's,
            so half the label was about something you cannot do to this thing;
            and it left you to find the row yourself among everything you own.
            Now it names one action and performs it — the row is selected,
            scrolled to and flashed on arrival. */}
        <Button
          size="md"
          variant="ghost"
          className="w-full justify-start"
          onClick={() => {
            useFileReveal.getState().reveal(n.document_id ?? n.id);
            workspace.openFiles();
          }}
        >
          <FolderTree className="h-4 w-4" /> Show this file in Folder View
        </Button>
        {isDoc && n.document_id && (
          <Button
            size="md"
            variant="ghost"
            className="w-full justify-start"
            onClick={() => void openSource()}
          >
            <ExternalLink className="h-4 w-4" /> Open the original file
          </Button>
        )}
      </Section>

      <SectionDivider />

      <Section title="Annotate" collapsible defaultOpen={false}>
        <Textarea
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={`A note about “${truncate(n.label, 24)}”… use [[links]]`}
        />
        <Button
          size="md"
          variant="secondary"
          className="w-full"
          disabled={!note.trim() || addNote.isPending}
          onClick={() => addNote.mutate()}
        >
          <StickyNote className="h-4 w-4" /> Save note to vault
        </Button>
      </Section>

      <SectionDivider />

      {/* DANGER — alone, last, and quiet until asked. It used to sit in the
          grid one cell away from "Pin". */}
      <Button
        size="md"
        variant="ghost"
        className={cn(
          "w-full justify-start",
          confirmingDelete ? "text-destructive" : "text-destructive/70 hover:text-destructive",
        )}
        disabled={del.isPending}
        onClick={() => setConfirmingDelete((v) => !v)}
      >
        <Trash2 className="h-4 w-4" /> Delete
      </Button>
      {confirmingDelete && (
        <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 animate-rise-in">
          <p className="flex items-start gap-2 text-[13px] leading-relaxed text-muted-foreground">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
            {isDoc
              ? `Delete “${truncate(n.label, 40)}” and its notes, AI outputs and file? You can re-upload it later.`
              : `Remove “${truncate(n.label, 40)}” from the graph?`}
          </p>
          <div className="flex gap-1.5">
            <Button
              size="md"
              variant="ghost"
              className="flex-1 bg-destructive/10 text-destructive hover:bg-destructive/20"
              disabled={del.isPending}
              onClick={() => del.mutate()}
            >
              <Trash2 className="h-4 w-4" />{" "}
              {del.isPending ? "Deleting…" : isDoc ? "Delete document" : "Delete"}
            </Button>
            <Button
              size="md"
              variant="ghost"
              className="flex-1"
              onClick={() => setConfirmingDelete(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ConnectBox({ sourceId, onDone }: { sourceId: string; onDone: () => void }) {
  const [q, setQ] = useState("");
  // Debounce so typing a target name fires one /api/search, not one per key.
  const search = useSearch(useDebouncedValue(q, 250));
  const connect = useMutation({
    mutationFn: (dstId: string) =>
      api.request("POST", "/api/edges", { src_id: sourceId, dst_id: dstId, type: "similar_to" }),
    onSuccess: () => {
      toast.success("Connected");
      onDone();
    },
    onError: (e) => toast.error("Could not connect", { description: String(e) }),
  });
  return (
    <div className="space-y-1.5 rounded-md border border-border bg-muted/40 p-2">
      <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Connect to… (search)" />
      <ul className="max-h-40 space-y-px overflow-y-auto">
        {(search.data?.hits ?? [])
          .filter((h) => h.node.id !== sourceId)
          .slice(0, 8)
          .map((h) => (
            <li key={h.node.id}>
              <button
                onClick={() => connect.mutate(h.node.id)}
                disabled={connect.isPending}
                className="flex w-full items-center gap-2 rounded px-2 py-1 text-left hover:bg-accent"
              >
                <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: NODE_COLOR[h.node.type] }} />
                <span className="truncate text-[12px] text-foreground">{h.node.label}</span>
                <span className="ml-auto font-mono text-[9px] uppercase text-muted-foreground">{h.node.type}</span>
              </button>
            </li>
          ))}
        {q.trim().length >= 2 && search.data?.hits.length === 0 && (
          <li className="px-2 py-1 text-2xs text-muted-foreground">No matches.</li>
        )}
      </ul>
    </div>
  );
}

function EdgeInspector({ edgeId }: { edgeId: string }) {
  const link = useGraph((s) => s.links.get(edgeId));
  const nodes = useGraph((s) => s.nodes);
  const [editing, setEditing] = useState(false);
  const [draftLabel, setDraftLabel] = useState("");
  const [draftType, setDraftType] = useState("");
  const ontology = useGraphOntology();

  const confirm = useMutation({
    mutationFn: () => api.confirmEdge(edgeId),
    onSuccess: () => {
      useGraph.getState().confirmEdgeLocal(edgeId);
      toast.success("Edge confirmed");
    },
  });
  const save = useMutation({
    mutationFn: () =>
      api.request<EdgeOut>("PATCH", `/api/edges/${edgeId}`, {
        label: draftLabel,
        type: draftType,
      }),
    onSuccess: (updated) => {
      // Refresh the in-graph edge so its new type/label render immediately.
      useGraph.getState().mergeBatch([], [updated]);
      setEditing(false);
      toast.success("Edge updated");
      void queryClient.invalidateQueries({ queryKey: keys.overview });
      // A changed edge type can re-shape the orbit layout — mark it stale.
      void queryClient.invalidateQueries({ queryKey: keys.orbits });
    },
    onError: (e) => toast.error("Could not update edge", { description: String(e) }),
  });
  const del = useMutation({
    mutationFn: () => api.request("DELETE", `/api/edges/${edgeId}`),
    onSuccess: () => {
      useGraph.getState().removeEdge(edgeId);
      // Deleting a connection invalidates the orbit layout.
      void queryClient.invalidateQueries({ queryKey: keys.orbits });
      toast.success("Edge deleted");
    },
    onError: (e) => toast.error("Could not delete edge", { description: String(e) }),
  });

  if (!link) return null;
  const e: EdgeOut = link.edge;
  const src = nodes.get(e.src_id);
  const dst = nodes.get(e.dst_id);
  const startEdit = () => {
    setDraftLabel(e.label ?? "");
    setDraftType(e.type);
    setEditing(true);
  };
  const rules = ontology.data?.edge_types;
  const legal = src && dst ? authorableTypes(rules, src.type, dst.type) : [];
  const typeOptions = legal.some((r) => r.type === e.type)
    ? legal
    : [{ type: e.type, phrase: edgePhrase(rules, e.type), authorable: false, pairs: [] }, ...legal];

  return (
    <div className="flex flex-col gap-3 p-3.5">
      <div className="flex items-center gap-2">
        <Badge>edge · {humanizeEdgeType(e.type)}</Badge>
        {e.status === "suggested" && <Badge className="border-primary/40 text-primary">suggested</Badge>}
        <div className="ml-auto flex items-center gap-0.5">
          {!editing && (
            <Tip label="Edit relationship" side="left">
              <Button
                size="icon"
                variant="ghost"
                className={cn("h-7 w-7 shrink-0", iconFocus)}
                onClick={startEdit}
                aria-label="Edit relationship"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </Tip>
          )}
          <CollapseButton />
        </div>
      </div>
      <div className="rounded-md border border-border bg-muted/40 p-3 text-[13.5px] leading-relaxed">
        <span className="text-foreground">{src?.label ?? e.src_id.slice(0, 8)}</span>
        <span className="mx-1.5 font-mono text-2xs text-primary">
          —{humanizeEdgeType(e.label?.trim() || e.type)}→
        </span>
        <span className="text-foreground">{dst?.label ?? e.dst_id.slice(0, 8)}</span>
      </div>

      {editing ? (
        <div className="flex flex-col gap-2 rounded-md border border-border bg-muted/30 p-2.5">
          <label className="font-mono text-2xs uppercase tracking-wide text-muted-foreground">
            Relationship type
          </label>
          {/* Only the relationships that are LEGAL between these two node
              types, read from the served matrix. The list used to be every
              edge type the palette knew, so most choices were rejected by the
              write chokepoint after the user had already committed to one —
              a picker offering answers the system refuses. The current type is
              always kept in the list even if the pair has since narrowed, so
              opening the editor can never silently retype an existing edge. */}
          <select
            value={draftType}
            onChange={(ev) => setDraftType(ev.target.value)}
            className="w-full rounded-md border border-border bg-input px-2 py-1.5 text-[12.5px] text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          >
            {typeOptions.map((t) => (
              <option key={t.type} value={t.type}>
                {t.phrase}
              </option>
            ))}
          </select>
          <label className="mt-1 font-mono text-2xs uppercase tracking-wide text-muted-foreground">
            Label (optional)
          </label>
          <Input
            autoFocus
            value={draftLabel}
            maxLength={200}
            placeholder="e.g. cites for methodology"
            onChange={(ev) => setDraftLabel(ev.target.value)}
            onKeyDown={(ev) => {
              if (ev.key === "Enter") save.mutate();
              if (ev.key === "Escape") setEditing(false);
            }}
          />
          <div className="mt-1 flex gap-1.5">
            <Button size="md" variant="default" className="flex-1" onClick={() => save.mutate()} disabled={save.isPending}>
              <Check className="h-4 w-4" /> Save
            </Button>
            <Button size="md" variant="ghost" onClick={() => setEditing(false)} disabled={save.isPending}>
              <X className="h-4 w-4" /> Cancel
            </Button>
          </div>
        </div>
      ) : (
        <dl className="space-y-1.5 font-mono text-[11px] text-muted-foreground">
          {e.label && (
            <div className="flex justify-between gap-2">
              <dt>label</dt>
              <dd className="text-right text-foreground">{e.label}</dd>
            </div>
          )}
          <div className="flex justify-between"><dt>type</dt><dd className="text-foreground">{e.type}</dd></div>
          <div className="flex justify-between"><dt>origin</dt><dd className="text-foreground">{e.origin}</dd></div>
          {e.confidence !== null && (
            <div className="flex justify-between"><dt>confidence</dt><dd className="text-foreground">{e.confidence.toFixed(2)}</dd></div>
          )}
          {e.evidence?.quote != null && (
            <p className="mt-2 border-l-2 border-border pl-2 italic text-muted-foreground">
              “{String(e.evidence.quote).slice(0, 160)}”
            </p>
          )}
        </dl>
      )}

      <div className="flex gap-1.5">
        {e.status === "suggested" && (
          <Button size="md" variant="default" className="flex-1" onClick={() => confirm.mutate()} disabled={confirm.isPending}>
            <Check className="h-4 w-4" /> Confirm
          </Button>
        )}
        <Button
          size="md"
          variant="ghost"
          className="flex-1 text-destructive/80 hover:text-destructive"
          onClick={() => del.mutate()}
          disabled={del.isPending}
        >
          <Trash2 className="h-4 w-4" /> Delete edge
        </Button>
      </div>
    </div>
  );
}
